import siteListStyles from './site-list.styles.css?inline';
import '../../../components/state-badge/agwa-state-badge.js';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { ApiSiteMaintenance } from '../../../api/core/api-site-maintenance';
import type { MemberSiteListItem } from '../../../api/agwa-api-model';
import { router, resolveRouterPath } from '../../../router';
import {
  collapseOperationTypeCodes,
  mapOrganizationTypeLabels,
} from '../shared/organization-type-labels';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '../../../components/search/agwa-search';
import '../../../components/empty-state/agwa-empty-state';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';
import { styles as sharedStyles } from '../../../styles/shared-styles';

import '../../../components/data-table/data-table';
import type {
  DataTableColumnDef,
  DataTable,
} from '../../../components/data-table/data-table';
import type { DataTableDataProvider } from '../../../components/data-table/data-table-provider';
import { terms } from '../../../language/terms';
import { preservedCasing } from '../../../language/preserved-casing';

@customElement('site-list')
export class SiteList extends LitElement {
  static styles = [sharedStyles, unsafeCSS(siteListStyles)];

  @property({ type: String }) groupId = '';

  @state() private sites: MemberSiteListItem[] = [];
  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private search = '';
  @state() private statusFilter: 'all' | 'active' | 'inactive' = 'all';
  @state() private totalCount = 0;
  @state() private sitesProvider?: DataTableDataProvider<MemberSiteListItem>;

  connectedCallback() {
    super.connectedCallback();
    if (this.groupId) {
      void this.fetchSites();
    }
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('groupId') && this.groupId) {
      void this.fetchSites();
    }
  }

  /** Column definitions for the sites table */
  private get siteColumns(): DataTableColumnDef[] {
    return [
      {
        title: 'Name',
        fieldName: 'siteName',
        width: '250px',
        fixed: 'left',
        sortable: true,
      },
      {
        title: preservedCasing(`${terms.imis.value} Ref`),
        fieldName: 'siteImisReference',
        width: '140px',
      },
      {
        title: terms.userGroupDomain.plural,
        fieldName: 'organizationTypes',
        width: '200px',
        displayValue: (_table: DataTable, item: MemberSiteListItem) =>
          item.organizationTypes.length > 0
            ? html`<div class="org-types">
                ${mapOrganizationTypeLabels(
                  // PSS collapses to PSM anywhere it is shown (architect, 2026-09-17).
                  collapseOperationTypeCodes(item.organizationTypes)
                ).map((type) => html`<sl-tag size="small">${type}</sl-tag>`)}
              </div>`
            : html`<span class="muted">—</span>`,
      },
      {
        title: 'Location',
        fieldName: 'suburb',
        width: '160px',
        displayValue: (_table: DataTable, item: MemberSiteListItem) =>
          item.suburb || item.state
            ? html`<div class="location-cell">
                ${item.suburb ? html`<span>${item.suburb}</span>` : ''}
                ${
                  item.state
                    ? html`<span class="location-state">${item.state}</span>`
                    : ''
                }
              </div>`
            : html`<span class="muted">—</span>`,
      },
      {
        title: terms.staffUser.plural,
        fieldName: 'staffCount',
        width: '80px',
        displayValue: (_table: DataTable, item: MemberSiteListItem) =>
          html`<span>${item.staffCount}</span>`,
      },
      {
        title: 'Active',
        fieldName: 'active',
        width: '100px',
        sortable: true,
        displayValue: (_table: DataTable, item: MemberSiteListItem) =>
          html`<agwa-state-badge
            domain="siteOperating"
            state=${item.active ? 'Active' : 'Inactive'}
          ></agwa-state-badge>`,
      },
    ];
  }

  async fetchSites() {
    if (!this.groupId) return;

    this.loading = true;
    this.error = null;
    this.sites = [];
    this.sitesProvider = undefined;

    try {
      const api = ApiFactory.authenticated(ApiSiteMaintenance);
      const resp = await api.getMemberSites(this.groupId);

      if (resp.success && resp.data?.items) {
        this.sites = resp.data.items;
        this.totalCount = resp.data.totalCount ?? resp.data.items.length;
        this.createSitesProvider();
      } else {
        this.error =
          resp.error || `Failed to load ${terms.loginSite.lowerPlural}`;
      }
    } catch (e: unknown) {
      this.error =
        e instanceof Error
          ? e.message
          : `Error loading ${terms.loginSite.lowerPlural}`;
    } finally {
      this.loading = false;
    }
  }

  /** Creates a client-side data provider that filters loaded sites */
  private createSitesProvider() {
    const search = this.search.trim().toLowerCase();
    const statusFilter = this.statusFilter;

    let filtered = [...this.sites];

    // Apply search filter
    if (search) {
      filtered = filtered.filter(
        (s) =>
          s.siteName?.toLowerCase().includes(search) ||
          s.siteImisReference?.toLowerCase().includes(search) ||
          s.suburb?.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter((s) => s.active);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter((s) => !s.active);
    }

    this.sitesProvider = async (request) => {
      // Apply sorting if requested
      const [sortDescriptor] = request.sort ?? [];
      let sorted = [...filtered];

      if (sortDescriptor) {
        const direction = sortDescriptor.direction === 'desc' ? -1 : 1;
        sorted.sort((a, b) => {
          const aVal = a[sortDescriptor.field as keyof MemberSiteListItem];
          const bVal = b[sortDescriptor.field as keyof MemberSiteListItem];
          if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
            return (aVal === bVal ? 0 : aVal ? -1 : 1) * direction;
          }
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return aVal.localeCompare(bVal) * direction;
          }
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return (aVal - bVal) * direction;
          }
          return 0;
        });
      }

      const start = request.sliceStart ?? 0;
      const pageSize = request.sliceLength ?? sorted.length;
      const page = sorted.slice(start, start + pageSize);

      return {
        items: page,
        totalCount: sorted.length,
        hasMore: start + page.length < sorted.length,
      };
    };
  }

  /**
   * Narrows the list to the search the box reports, once. The box reports one search twice -
   * `search-input` as the person types and `search-change` when they press Enter or clear - so a
   * value already searched is not searched again, which reloaded the table a second time
   * (#398 C15 d). A clear is reported as `search-clear` and then a change to nothing, so the change
   * finds the box already empty and is the one dropped.
   */
  private handleSearchInput(e: CustomEvent<{ value: string }>) {
    if (e.detail.value === this.search) return;
    this.search = e.detail.value;
    this.createSitesProvider();
  }

  private handleSearchClear() {
    this.search = '';
    this.createSitesProvider();
  }

  private handleStatusFilter(e: CustomEvent) {
    this.statusFilter = (e.target as HTMLInputElement).value as
      'all' | 'active' | 'inactive';
    this.createSitesProvider();
  }

  private handleAddSite() {
    // Navigate to site workspace with 'new' as siteId
    // The site-workspace will handle creating a new site
    void router?.navigate(
      resolveRouterPath(`maintenance/companies/${this.groupId}/sites/new`)
    );
  }

  private openSite(site: MemberSiteListItem) {
    void router?.navigate(
      resolveRouterPath(
        `maintenance/companies/${this.groupId}/sites/${site.siteId}`
      )
    );
  }

  private get hasNoMatches(): boolean {
    if (this.sites.length === 0) return false;
    const search = this.search.trim().toLowerCase();
    let filtered = [...this.sites];
    if (search) {
      filtered = filtered.filter((s) =>
        s.siteName?.toLowerCase().includes(search)
      );
    }
    if (this.statusFilter === 'active') {
      filtered = filtered.filter((s) => s.active);
    } else if (this.statusFilter === 'inactive') {
      filtered = filtered.filter((s) => !s.active);
    }
    return filtered.length === 0;
  }

  render() {
    return html`
      <div class="site-list">
        <div class="filter-bar">
          <div class="filters-left">
            <agwa-search
              .value=${this.search}
              placeholder="Search ${terms.loginSite.lowerPlural}..."
              style="--agwa-search-max-width:260px;"
              @search-input=${this.handleSearchInput}
              @search-change=${this.handleSearchInput}
              @search-clear=${this.handleSearchClear}
            ></agwa-search>
            <sl-radio-group
              value=${this.statusFilter}
              @sl-change=${this.handleStatusFilter}
            >
              <sl-radio-button value="all">All</sl-radio-button>
              <sl-radio-button value="active">Active</sl-radio-button>
              <sl-radio-button value="inactive">Inactive</sl-radio-button>
            </sl-radio-group>
            ${
              this.totalCount > 0
                ? html`<span class="muted">${this.totalCount} sites</span>`
                : ''
            }
          </div>
          <div class="actions-right">
            <sl-button variant="primary" @click=${this.handleAddSite}>
              <sl-icon slot="prefix" name="building-add"></sl-icon>
              Add ${terms.loginSite.value}
            </sl-button>
          </div>
        </div>

        ${
          this.loading
            ? html`<agwa-empty-state
                icon="hourglass-split"
                title="Loading"
                message="Fetching ${terms.loginSite.lowerPlural}..."
              ></agwa-empty-state>`
            : this.error
              ? html`<agwa-empty-state
                  icon="exclamation-triangle"
                  title="Error"
                  .message=${this.error}
                ></agwa-empty-state>`
              : this.sites.length === 0 && !this.search
                ? html`<agwa-empty-state
                    icon="building"
                    title="No ${terms.loginSite.lowerPlural} yet"
                    message="Add your first ${terms.loginSite.lower} to get started."
                  >
                    <sl-button
                      variant="primary"
                      size="small"
                      @click=${this.handleAddSite}
                    >
                      <sl-icon slot="prefix" name="building-add"></sl-icon>
                      Add ${terms.loginSite.value}
                    </sl-button>
                  </agwa-empty-state>`
                : this.hasNoMatches
                  ? html`<agwa-empty-state
                      icon="search"
                      title="No matches"
                      message="No ${terms.loginSite.lowerPlural} match your current filters."
                    ></agwa-empty-state>`
                  : this.sitesProvider
                    ? html`<agwa-lit-table
                        fill-height
                        .columns=${this.siteColumns}
                        .dataProvider=${this.sitesProvider}
                        .clickrows=${true}
                        @rowclick=${(
                          e: CustomEvent<{ item: MemberSiteListItem }>
                        ) => this.openSite(e.detail.item)}
                      ></agwa-lit-table>`
                    : html``
        }
      </div>
    `;
  }
}
